/**
 * Phase 532 — reading the engine's refusal, with NO browser underneath it.
 *
 * WHY THIS MODULE IS SEPARATE FROM `fetcher.ts`. This reader is pure string
 * work, but it used to live beside `fetchWithAuth`, which imports MSAL and
 * touches `fetch` / `Headers` / `window`. Any test that imported the reader
 * therefore dragged the whole browser surface into the console's DOM-FREE
 * typecheck project (`tsconfig.integration.json`, `types: ["node"]`,
 * `lib: ["ES2022"]`) and `pnpm run typecheck` went red on `Cannot find name
 * 'window'`. So the reader moved here, to a module that is DOM-free BY
 * CONSTRUCTION: zero imports, and no reference to `fetch`, `Response`,
 * `RequestInit`, `Headers`, `window`, `document`, `localStorage` or any MSAL
 * type. It joins `screens/_status.ts` and `api/chatRoutes.ts` as one of the
 * handful of `src` modules the node-only test tree may reach into.
 *
 * WHY THIS EXISTS AT ALL. Every internal-API refusal is an `ErrorResponse`
 * (`platform/workflow-engine/Http/InternalApiDtos.cs`) whose `message` is
 * written FOR the operator — it names what is owed and where to supply it. The
 * console used to throw all of that away and print one of two generic sentences,
 * so a refusal that said exactly what to do next arrived as "Could not record
 * this decision. Check the job status and try again." A screen that cannot
 * repeat the server's sentence turns a solvable refusal into a mystery.
 */

/** The engine's refusal, as the operator should read it. */
export interface ServerRefusal {
  /** The engine's closed-vocabulary code (`ErrorResponse.error_code`). */
  code: string;
  /** The engine's own plain-English sentence (`ErrorResponse.message`). */
  message: string;
}

/**
 * The `error instanceof ApiError` narrowing, done STRUCTURALLY — importing
 * `ApiError` from `fetcher.ts` is precisely what would pull the browser back in.
 * The shape mimicked here is the one `fetcher.ts` actually throws: an `Error`
 * whose `name` is `"ApiError"` and which carries a string `bodyText`.
 *
 * A duck-type is a lie waiting to happen, so it is not trusted: it is PINNED to
 * the real class by `test/decision-refusal.test.ts`, which reads `fetcher.ts` as
 * text and asserts `class ApiError extends Error`, the `bodyText` string member
 * and `this.name = "ApiError"` are all still there. Rename either side and that
 * test goes red, instead of every refusal silently degrading to the generic
 * sentence.
 */
function apiErrorBodyText(error: unknown): string | null {
  if (!(error instanceof Error)) return null;
  if (error.name !== "ApiError") return null;
  const bodyText = (error as Error & { bodyText?: unknown }).bodyText;
  return typeof bodyText === "string" ? bodyText : null;
}

/**
 * Read the engine's `{error_code, message}` off a thrown `ApiError`.
 *
 * Returns `null` — and the caller falls back to its generic copy — for anything
 * that is not an engine refusal with BOTH halves: a network failure, a 5xx with
 * an HTML body, a truncated response. Never throws: it is called from render.
 */
export function readServerRefusal(error: unknown): ServerRefusal | null {
  const bodyText = apiErrorBodyText(error);
  if (bodyText === null) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(bodyText);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;

  const body = parsed as Record<string, unknown>;
  const code = body["error_code"];
  const message = body["message"];
  if (typeof code !== "string" || code.length === 0) return null;
  if (typeof message !== "string" || message.length === 0) return null;

  return { code, message };
}
